/**
 * MaterialDetailPage
 * ==================
 * Phase 3: Material Intelligence Sheet
 * 
 * Editorial-style page, NOT a product page.
 * Displays Material DNA, SRE Intelligence, Applications.
 * 
 * Route: /materials/:slug
 * 
 * ALL content from Blueprint config:
 * - content.material.*
 * - ui.material.*
 * - logic.sre.*
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBlueprint } from '../context/BlueprintContext';
import { API } from '../App';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Material components
import MaterialHero from '../components/material/MaterialHero';
import MaterialGallery from '../components/material/MaterialGallery';
import MaterialDNA from '../components/material/MaterialDNA';
import MaterialApplications from '../components/material/MaterialApplications';
import SimilarityNavigation from '../components/material/SimilarityNavigation';
import SREPanel from '../components/material/SREPanel';

// Icons
import { RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';

// Styles
import '../styles/slabreality.css';

const MaterialDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { 
    loading: configLoading, 
    content, 
    ui,
    getState, 
    getButton,
    getRoute,
    buildRoute
  } = useBlueprint();
  
  // Content from config
  const pageContent = content?.material?.detail || {};
  const layoutConfig = ui?.material?.detail || {};
  
  // State
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch material by slug/id
  useEffect(() => {
    const fetchMaterial = async () => {
      if (!slug) {
        setError('No material ID provided');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Use Backend API to fetch single material
        const response = await axios.get(`${API}/materials/${slug}`);
        const data = response.data;
        
        if (data && data.material_id) {
          // Normalize material data structure
          const normalizedMaterial = {
            id: data.material_id || data.id,
            material_id: data.material_id,
            name: data.name,
            stone_type: data.stone_type,
            description: data.description,
            finishes: data.finishes || [],
            applications: data.applications || [],
            color: data.color,
            origin: data.origin,
            images: data.images || [],
            texture_images: data.texture_images || [],
            render_images: data.render_images || [],
            dna: data.dna || null
          };
          
          setMaterial(normalizedMaterial);
          console.log('✅ Material loaded:', normalizedMaterial.name);
        } else {
          setError('Material not found');
        }
      } catch (err) {
        console.error('Failed to load material:', err);
        setError(err.response?.status === 404 ? 'Material not found' : err.message);
      }
      
      setLoading(false);
    };
    
    fetchMaterial();
  }, [slug]);
  
  // Handlers
  const handleSave = () => {
    console.log('💾 Save material:', material?.id);
    // TODO: Implement save to workspace
  };
  
  const handleShare = () => {
    console.log('📤 Share material:', material?.id);
    // Copy link to clipboard
    navigator.clipboard.writeText(window.location.href);
  };
  
  const handleOpenSRE = () => {
    console.log('⚡ Open SRE for:', material?.id);
    const sreRoute = getRoute('sre', '/sre');
    navigate(`${sreRoute}?material=${material?.id}`);
  };
  
  const handleSaveMaterial = (materialId) => {
    console.log('💾 Save related material:', materialId);
    // TODO: Implement save
  };
  
  // Loading state - only wait for material, not Blueprint config
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={32} className="text-[var(--gold)] animate-spin mx-auto mb-4" />
          <p className="text-[var(--t3)] text-sm">
            Caricamento materiale...
          </p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[var(--cream)]">
        <Header />
        <div className="pt-[68px] flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={28} className="text-red-500" />
            </div>
            <h2 className="font-[var(--ff-h)] text-2xl font-semibold text-[var(--t1)] mb-2">
              {pageContent.error_title || 'Materiale non trovato'}
            </h2>
            <p className="text-[var(--t3)] mb-6">
              {pageContent.error_description || 'Il materiale richiesto non esiste o è stato rimosso.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[var(--border)] text-[var(--t1)] rounded-lg hover:border-[var(--gold)] transition-colors"
              >
                <ArrowLeft size={16} />
                {getButton('back', 'Indietro')}
              </button>
              <button
                onClick={() => navigate(getRoute('explorer', '/materials'))}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--dark)] text-white rounded-lg hover:bg-[var(--dark)]/90 transition-colors"
              >
                {getButton('explore_materials', 'Esplora Materiali')}
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // No material
  if (!material) {
    return null;
  }
  
  // Prepare images array - priority: image_main > images > image_url
  const images = (() => {
    const result = [];
    // Add image_main first (R2)
    if (material.image_main) result.push(material.image_main);
    // Add images array
    if (Array.isArray(material.images)) {
      material.images.forEach(img => {
        if (img && !result.includes(img)) result.push(img);
      });
    }
    // Add image_url if not already included
    if (material.image_url && !result.includes(material.image_url)) {
      result.push(material.image_url);
    }
    return result.length > 0 ? result : ['/placeholder-material.svg'];
  })();
  
  return (
    <div className="min-h-screen bg-[#0A0A09]" data-testid="material-detail-page">
      {/* Dark Navbar Override */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A09]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-white/20 flex items-center justify-center text-white text-sm font-semibold">
              SR
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-[3px] text-white">SLABREALITY</div>
              <div className="text-[7px] tracking-[1px] text-white/40">THE REAL SLAB</div>
            </div>
          </a>
        </div>
      </nav>
      
      <main className="pt-[68px]">
        {/* Hero Section - Full Immersive */}
        <MaterialHero
          material={material}
          onSave={handleSave}
          onShare={handleShare}
          onOpenSRE={handleOpenSRE}
        />
        
        {/* Main Content - Dark Theme */}
        <div className="max-w-[1400px] mx-auto px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column - Material Intelligence */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* Description - Elegant */}
              {material.description && (
                <section>
                  <p className="text-lg text-white/70 leading-relaxed font-light">
                    {material.description}
                  </p>
                </section>
              )}
              
              {/* DNA Panel - Integrated */}
              <MaterialDNA material={material} />
              
              {/* Gallery - Minimal */}
              {images.length > 1 && (
                <section className="pt-8 border-t border-white/5">
                  <h3 className="text-[11px] font-bold tracking-[3px] uppercase text-[#D6C1A5] mb-6">
                    {pageContent.gallery_title || 'Galleria'}
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {images.slice(0, 6).map((img, i) => (
                      <div key={i} className="aspect-square overflow-hidden">
                        <img 
                          src={img} 
                          alt={`${material.name} ${i + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
              
            </div>
            
            {/* Right Column - SRE Intelligence + Specs */}
            <div className="lg:col-span-5 space-y-6">
              
              {/* SRE Panel - Core Element */}
              <div className="lg:sticky lg:top-24">
                <SREPanel 
                  material={material} 
                  expanded={true}
                />
                
                {/* Quick Specs - Dark */}
                <div className="mt-6 bg-[#0F0F0E] border border-white/5 p-6">
                  <h4 className="text-[11px] font-bold tracking-[3px] uppercase text-white/40 mb-5">
                    {pageContent.specs_title || 'Specifiche'}
                  </h4>
                  
                  <dl className="space-y-4">
                    {material.category && (
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <dt className="text-sm text-white/40">{pageContent.spec_category || 'Categoria'}</dt>
                        <dd className="text-sm text-white capitalize">{material.category}</dd>
                      </div>
                    )}
                    
                    {material.origin && (
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <dt className="text-sm text-white/40">{pageContent.spec_origin || 'Origine'}</dt>
                        <dd className="text-sm text-white">{material.origin}</dd>
                      </div>
                    )}
                    
                    {material.finish && (
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <dt className="text-sm text-white/40">{pageContent.spec_finish || 'Finitura'}</dt>
                        <dd className="text-sm text-white">{material.finish}</dd>
                      </div>
                    )}
                    
                    {material.thickness && (
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <dt className="text-sm text-white/40">{pageContent.spec_thickness || 'Spessore'}</dt>
                        <dd className="text-sm text-white">{material.thickness}</dd>
                      </div>
                    )}
                    
                    {material.supplier && (
                      <div className="flex justify-between items-center py-2">
                        <dt className="text-sm text-white/40">{pageContent.spec_supplier || 'Fornitore'}</dt>
                        <dd className="text-sm text-white">{material.supplier}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                
                {/* CTA Card - Premium */}
                <div className="mt-6 bg-gradient-to-br from-[#D6C1A5]/10 to-transparent border border-[#D6C1A5]/20 p-6">
                  <h4 className="text-lg font-light text-white mb-2">
                    {pageContent.cta_title || 'Interessato?'}
                  </h4>
                  <p className="text-sm text-white/50 mb-5">
                    {pageContent.cta_description || 'Richiedi informazioni o un campione.'}
                  </p>
                  <button className="w-full py-3 bg-[#D6C1A5] text-[#0A0A09] text-sm font-semibold tracking-wide hover:bg-[#E0CBB5] transition-colors">
                    {getButton('request_info', 'Richiedi Informazioni')}
                  </button>
                </div>
              </div>
              
            </div>
          </div>
        </div>
        
        {/* SIMILARITY NAVIGATION SYSTEM - Primary Navigation */}
        <section className="border-t border-white/5 py-16 bg-[#0A0A09]">
          <div className="max-w-[1400px] mx-auto px-8">
            {/* Section Header */}
            <div className="mb-10">
              <span className="text-[10px] font-bold tracking-[3px] uppercase text-[#D6C1A5] block mb-3">
                MATERIAL NAVIGATION
              </span>
              <h2 className="text-3xl font-light text-white">
                Scopri Alternative
              </h2>
              <p className="text-white/40 mt-2 max-w-xl">
                Esplora materiali con caratteristiche simili, alternative funzionali e contrasti progettuali.
              </p>
            </div>
            
            {/* Similarity Navigation Component */}
            <SimilarityNavigation
              sourceMaterial={material}
              limits={{ similar: 3, strongAlternatives: 3, contrastAlternatives: 3 }}
            />
          </div>
        </section>
        
      </main>
      
      {/* Dark Footer */}
      <footer className="border-t border-white/5 py-8 bg-[#0A0A09]">
        <div className="max-w-[1400px] mx-auto px-8 text-center">
          <p className="text-xs text-white/30">
            © 2026 SlabReality. THE REAL SLAB. IN YOUR SPACE.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MaterialDetailPage;
